// noinspection JSVoidFunctionReturnValueUsed
import dataset from './datasets/dataset.json';
import azure from 'azure-storage';

const MicrosoftGraph = require('@microsoft/microsoft-graph-client');
const PROJECTS_STORAGE_NAME = 'projects';
const SUB_PROJECTS_STORAGE_NAME = 'subprojects';
const USERS_STORAGE_NAME = 'userdata';
const BLOB_STORAGE_BASE_URL = `https://${process.env.REACT_APP_ENVIRONMENT}cultec.blob.core.windows.net`;
const TABLE_STORAGE_BASE_URL = `https://${process.env.REACT_APP_ENVIRONMENT}cultec.table.core.windows.net`;
const BARRACUDA_EMAIL_ENDPOINT = `https://${process.env.REACT_APP_API_URL} /api/automationbarracudaemail`;
const AUTHORIZER_ENDPOINT = `https://${process.env.REACT_APP_API_URL}/api/authorizer`;
const ITOKENS_ENDPOINT = `https://${process.env.REACT_APP_API_URL}/api/itokens`;
const TASK_SUBMIT_ENDPOINT = `https://${process.env.REACT_APP_API_URL}/api/automationstart`;
const TASK_STATUS_ENDPOINT = `https://${process.env.REACT_APP_API_URL}/api/automationstatus`;
const TASK_PROCESSING_LOG_ENDPOINT = `https://${process.env.REACT_APP_API_URL}/api/automationalert`;
const MULTIBED_COMMON_ENDPOINT = `https://${process.env.REACT_APP_API_URL}/api/multibedcommon`;

const AGREEMENT_LOG_STORAGE_NAME = 'agreementlog';

/**
 * MiniCADAPI Class and methods to communicate with the backend
 */
export default class MiniCADAPI {
  static fetch() {
    return new Promise((resolve) => {
      setTimeout(() => resolve(dataset), 1000);
    });
  }

  /**
   * This function ...
   *
   * @param {Object} logDataObject, the data to be inserted into the table.
   * @param {Object} userData, user data.
   * @param idToken
   * @param accesstoken token The SAS access token in order to access the table.
   */
  static async postProjectLogRecord(logDataObject, userData, idToken, accesstoken) {
    let entity = {
      partitionKey: userData.userId,
      rowKey: userData.projectId,
      region: userData.regionId,
      language: userData.languageId,
      alertLevel: userData.alertLevel,
      timestamp: new Date(),
    };
    entity = { ...logDataObject, ...entity };
    let result;
    try {
      let response = await fetch(TASK_PROCESSING_LOG_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({
          ...entity,
        }),
        headers: {
          idToken: idToken,
          email: logDataObject['email'],
          accesstoken: accesstoken,
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      result = await response.json();
    } catch (e) {
      console.error(`ERROR - TASK_PROCESSING_LOG_ENDPOINT - response not a json: ${e.message}`);
    }
    return result;
  }

  static async sendBarracudaEmail(fullName, email, partitionKey, rowKey) {
    let result = false;
    try {
      let response = await fetch(BARRACUDA_EMAIL_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({
          fullName,
          email,
          partitionKey,
          rowKey,
        }),
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });

      /* await for the resolution of content */
      result = response.ok;
    } catch (e) {
      console.error(`ERROR - BARRACUDA_EMAIL_ENDPOINT: ${e.message}`);
    }
    return result;
  }

  /**
   * Triggers Automation Start on the server
   * @param {*} idtoken
   * @param {*} email
   * @param {*} accesstoken
   * @param partitionKey
   * @param projectId
   * @param hydroCADRequest
   */
  static async postAutomationStart(idtoken, email, accesstoken, partitionKey, projectId, hydroCADRequest = false) {
    console.log(`postAutomationStart, hydroCADRequest:${hydroCADRequest}`);
    let response = await fetch(TASK_SUBMIT_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        partitionKey,
        projectId,
        hydroCADRequest,
      }),
      headers: {
        idtoken,
        email,
        accesstoken,
        'Content-Type': 'application/json; charset=utf-8',
      },
    });
    let result;
    try {
      result = await response.json();
    } catch (e) {
      result = {};
    }

    return result;
  }

  /**
   * Requests task status from azure function by POST method
   *
   * @param workItemId
   * @param partitionKey
   * @param projectId
   * @param isMultiBedProject
   */
  static async getTaskStatus(workItemId, partitionKey, projectId, isMultiBedProject) {
    let response = await fetch(TASK_STATUS_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        workItemId,
        partitionKey,
        projectId,
        multiBed: isMultiBedProject,
      }),
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
    });
    let result = await response.text();
    console.log(`result`, result);
    return result;
  }

  /**
   * Requests the Resources Tokens by POST method.
   * @param {*} idtoken
   * @param {*} email
   * @param {*} accesstoken
   */
  static async getResourcesTokens(idtoken, email, accesstoken) {
    let response = await fetch(AUTHORIZER_ENDPOINT, {
      method: 'POST',
      headers: {
        idtoken,
        email,
        accesstoken,
      },
    });

    let resourcesTokens = await response.json();
    return resourcesTokens;
  }

  /**
   * Requests Internal Resources Tokens by POST method
   * @param {*} idtoken
   * @param {*} email
   * @param {*} accesstoken
   * @param otherUserOID
   */
  static async getInternalResourcesTokens(idtoken, email, accesstoken, otherUserOID) {
    let response = await fetch(ITOKENS_ENDPOINT, {
      method: 'POST',
      headers: {
        idtoken,
        email,
        accesstoken: idtoken,
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        container: otherUserOID,
      }),
    });

    let resourcesTokens = await response.json();
    return resourcesTokens;
  }

  /**
   * Prepares the BlobService object using the host Uri and the SAS credentials provided.
   * @param {*} token
   */
  static buildBlobService(token) {
    return azure
      .createBlobServiceWithSas(BLOB_STORAGE_BASE_URL, token)
      .withFilter(new azure.ExponentialRetryPolicyFilter());
  }

  /**
   * Prepares the TableService object using the host Uri and the SAS credentials.
   * @param {string} token - MSAL token
   */
  static buildTableService(token) {
    return azure
      .createTableServiceWithSas(TABLE_STORAGE_BASE_URL, token)
      .withFilter(new azure.ExponentialRetryPolicyFilter());
  }

  /**
   * This function uploads a project file to backend storage.
   *
   * @param {String} container SimpleLoader name in backend storage, this is the user's iod.
   * @param {String} filename The filename for the particular file to be put in backend storage(project meta name).
   * @param project
   * @param {String} token The SAS access token in order to access the container.
   * @param {Function} progressFn The progress function to report back upload progress.
   */
  static async putProjectFile(container, filename, project, token, progressFn) {
    /* create blob service from SAS token */
    const blobService = MiniCADAPI.buildBlobService(token);
    /* await for the put file promise to be resolved */
    const result = await new Promise((resolve, reject) => {
      const handler = blobService.createBlockBlobFromText(
        container,
        `${filename}.json`,
        JSON.stringify(project),
        function (error, result) {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        },
      );
      /* the upload progress handler */
      handler.on('progress', () => {
        if (progressFn) {
          progressFn(handler.getCompletePercent());
        }
      });
    });
    /* return result */
    return result;
  }

  /**
   * This function downloads a project file from backend storage.
   *
   * @param {String} container SimpleLoader name in backend storage, this is the user's iod.
   * @param {String} filename The filename for the particular file to be fetch from backend storage(project meta name).
   * @param {String} token The SAS access token in order to access the container.
   * @param {Function} progressFn The progress function to report back download progress.
   */
  static async getProjectFile(container, filename, token, progressFn) {
    const blobService = MiniCADAPI.buildBlobService(token);
    const project = await new Promise((resolve, reject) => {
      blobService.getBlobToText(container, `${filename}.json`, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(JSON.parse(result));
        }
      });
    });
    return project;
  }

  /**
   * This function fetches project records from backend table storage.
   *
   * @param {String} partitionKey Unique key used in table storage, this is the user's iod.
   * @param {String} token The SAS access token in order to access the table.
   */
  static async getProjectRecords(partitionKey, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery().where('PartitionKey eq ?', partitionKey).and('active eq ?', true);
      tableService.queryEntities(PROJECTS_STORAGE_NAME, query, null, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(result.entries);
        }
      });
    });
    return records;
  }

  /**
   * This function fetches a single project record from backend table storage.
   *
   * @param {String} partitionKey Unique key used in table storage, this is the user's iod.
   * @param {String} projectId The project identifier to be fetch.
   * @param {String} token The SAS access token in order to access the table.
   */
  static async getProjectRecord(partitionKey, projectId, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery().where('PartitionKey eq ?', partitionKey).and('RowKey eq ?', projectId);
      tableService.queryEntities(PROJECTS_STORAGE_NAME, query, null, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(result.entries);
        }
      });
    });
    return records[0];
  }

  /**
   * This function insert a project record into backend table storage.
   *
   * @param {Object} entity the data to be inserted into the table.
   * @param {String} token The SAS access token in order to access the table.
   */
  static async putProjectRecord(entity, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const result = await new Promise((resolve, reject) => {
      tableService.insertOrReplaceEntity(PROJECTS_STORAGE_NAME, entity, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
    return result;
  }

  // USERS
  static async getAllUsersRecord(token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery().select(['PartitionKey']);

      tableService.queryEntities(USERS_STORAGE_NAME, query, null, function (error, result) {
        if (error) {
          reject(error);
        } else {
          let arrOfIds = [];
          for (const entry of result.entries) {
            arrOfIds.push(entry.PartitionKey._);
          }
          resolve(arrOfIds);
        }
      });
    });
    return records;
  }

  // Get all users' records including their department
  static async getAllUsersWithDepartments(token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery().select(['PartitionKey', 'Department']);
      tableService.queryEntities(USERS_STORAGE_NAME, query, null, function (error, result) {
        if (error) {
          reject(error);
        } else {
          let users = result.entries.map((entry) => ({
            userId: entry.PartitionKey._,
            department: entry.Department._,
          }));
          resolve(users);
        }
      });
    });
    return records;
  }

  /**
   * TODO: Documentation.
   * @param partitionKey
   * @param token
   * @returns {Promise<unknown>}
   */
  static async getUserRecord(partitionKey, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery()
        .where('PartitionKey eq ?', partitionKey);
      tableService.queryEntities(
        USERS_STORAGE_NAME,
        query,
        null,
        function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(result.entries);
        }
      });
    });
    if (records && records[0]) {
      return records[0];
    } else {
      const addedRec = await MiniCADAPI.upsertUserRecord(
        {
          userId: partitionKey,
        },
        token,
      );
      return addedRec;
    }
  }

  /**
   * TODO: Documentation.
   * @param userData
   * @param token
   * @returns {Promise<unknown>}
   */
  static async upsertUserRecord(userData, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const entGen = azure.TableUtilities.entityGenerator;

    let entity = {
      PartitionKey: entGen.String(userData.userId),
      RowKey: entGen.String(''),
    };

    if (userData.regionId) entity.region = entGen.String(userData.regionId);

    if (userData.languageId) entity.language = entGen.String(userData.languageId);

    if (userData.unit) entity.unit = entGen.String(userData.unit);

    if (userData.county) entity.county = entGen.String(userData.county);

    const result = await new Promise((resolve, reject) => {
      tableService.insertOrReplaceEntity(USERS_STORAGE_NAME, entity, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(entity);
        }
      });
    });
    return result;
  }

  /**
   * Retrieve all users from graph API if and only if administrator.
   *
   * @param token
   */
  static async getAllUsers(token) {
    const users = await new Promise(async (resolve) => {
      const PageIterator = MicrosoftGraph.PageIterator;
      let client = MicrosoftGraph.Client.init({
        defaultVersion: 'v1.0',
        authProvider: (done) => {
          done(null, token);
        },
      });

      let response = await client
        .api('/users')
        .version('beta')
        .select(
          'id',
          'givenName',
          'surname',
          'state',
          'city',
          'country',
          'identities',
          'companyName',
          'mobilePhone',
          'createdDateTime',
          'signInSessionsValidFromDateTime',
          //'*'
        )
        .top(999)
        .get();
      let users = [];
      let pageIterator = new PageIterator(client, response, (data) => {
        users.push(data);
        if (pageIterator.nextLink === undefined && pageIterator.collection.length === 0) {
          resolve(users);
          return false;
        }
        return true;
      });
      pageIterator.iterate();
    });
    return users;
  }

  // Get users data
  static async getB2CUser(token, userId) {
    let client = MicrosoftGraph.Client.init({
      defaultVersion: 'v1.0',
      authProvider: (done) => {
        done(null, token);
      },
    });
    let user = await client
      .api(`/users/${userId}`)
      .select(
        'id,displayName,givenName,surname,mail,jobTitle,companyName,businessPhones,city,country,department,jobTitle,mobilePhone,postalCode,state,mobilePhone'
      )
      .get();
    return user;
  }

  // Get all users data
  static async getaAllB2CUser(token, userId) {
    let client = MicrosoftGraph.Client.init({
      defaultVersion: 'v1.0',
      authProvider: (done) => {
        done(null, token);
      },
    });
    // let user = await client
    //   .api(`/users/${userId}`)
    //   .select(
    //     // "*"
    //     'id,displayName,givenName,surname,mail,jobTitle,companyName,businessPhones,city,country,department,jobTitle,mobilePhone,postalCode,state,mobilePhone'
    //   )
    //   .get();
    return null;
  }

  // Update users data
  static async updateB2CUser(token, userId, dataToUpdate) {
    let client = MicrosoftGraph.Client.init({
      defaultVersion: 'v1.0',
      authProvider: (done) => {
        done(null, token);
      },
    });

    let userUpdated = await client.api(`/users/${userId}`).version('beta').patch(dataToUpdate);

    return userUpdated;
  }

  // PROJECT,  MULTIBED and SUB PROJECT
  static async postMultiBedCommonsRecord(multiBedCommonObject, userData, idToken, accesstoken) {
    let entity = {
      partitionKey: userData.userId,
      rowKey: userData.projectId,
    };
    entity = { ...multiBedCommonObject, ...entity };
    let result;
    try {
      let response = await fetch(MULTIBED_COMMON_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify({
          ...entity,
        }),
        headers: {
          idToken: idToken,
          accesstoken: accesstoken,
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      result = await response.json();
    } catch (e) {
      console.error(`ERROR - MULTIBED_COMMON_ENDPOINT - response not a json: ${e.message}`);
    }
    return result;
  }

  /**
   * This function insert a project record into backend table storage.
   *
   * @param {Object} entity the data to be inserted into the table.
   * @param {String} token The SAS access token in order to access the table.
   */
  static async insertOrReplaceSubProject(entity, token) {
    try {
      const tableService = MiniCADAPI.buildTableService(token);
      const result = await new Promise((resolve, reject) => {
        tableService.insertOrReplaceEntity(SUB_PROJECTS_STORAGE_NAME, entity, function (error, result) {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      });
      return result;
    } catch (error) {
      console.error('MiniCADAPI.insertOrReplaceSubProject() :: ', error.message);
    }
  }

  /**
   * Saves SubProjectBlob
   * @param container
   * @param filename
   * @param project
   * @param token
   * @param progressFn
   * @returns {Promise<unknown>}
   */
  static async putSubProjectFile(container, filename, project, token, progressFn) {
    try {
      const blobService = MiniCADAPI.buildBlobService(token);
      return await new Promise((resolve, reject) => {
        const handler = blobService.createBlockBlobFromText(
          container,
          `${filename}.json`,
          JSON.stringify(project),
          function (error, result) {
            if (error) {
              reject(error);
            } else {
              resolve(result);
            }
          },
        );
        handler.on('progress', () => {
          if (progressFn) {
            progressFn(handler.getCompletePercent());
          }
        });
      });
    } catch (error) {
      console.error(`MiniCADAPI.insertOrReplaceSubProject() :: ${error.message}`);
    }
  }

  static async getSubProjectRecords(partitionKey, projectId, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery()
        .where('PartitionKey eq ?', partitionKey)
        .and('parentProjectId eq ?', projectId)
        .and('active eq ?', true);
      tableService.queryEntities(SUB_PROJECTS_STORAGE_NAME, query, null, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(result.entries);
        }
      });
    });
    return records;
  }

  static async getUserSubProjectRecords(partitionKey, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const records = await new Promise((resolve, reject) => {
      let query = new azure.TableQuery().where('PartitionKey eq ?', partitionKey).and('active eq ?', true);
      tableService.queryEntities(SUB_PROJECTS_STORAGE_NAME, query, null, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(result.entries);
        }
      });
    });
    return records;
  }

  static async getSubProjectRecord(partitionKey, projectId, token) {
    try {
      const tableService = MiniCADAPI.buildTableService(token);
      const records = await new Promise((resolve, reject) => {
        let query = new azure.TableQuery().where('PartitionKey eq ?', partitionKey).and('RowKey eq ?', projectId);
        tableService.queryEntities(SUB_PROJECTS_STORAGE_NAME, query, null, function (error, result) {
          if (error) {
            reject(error);
          } else {
            resolve(result.entries);
          }
        });
      });
      return records[0];
    } catch (error) {
      console.error(`MiniCADAPI.getSubProjectRecord() :: ${error.message}`);
    }
  }

  static async getSubProjectFile(container, filename, token, progressFn) {
    try {
      const blobService = MiniCADAPI.buildBlobService(token);
      const subProject = await new Promise((resolve, reject) => {
        blobService.getBlobToText(container, `${filename}.json`, function (error, result) {
          if (error) {
            reject(error);
          } else {
            resolve(JSON.parse(result));
          }
        });
      });
      return subProject;
    } catch (error) {
      console.error(`MiniCADAPI.getSubProjectFile() :: ${error.message}`);
    }
  }

  static async latestOutputExists(partitionKey, projectId, projectName, tableToken, blobToken) {
    let result = false;
    try {
      let projRecord = await MiniCADAPI.getSubProjectRecord(partitionKey, projectId, tableToken);
      if (projRecord) {
        if (
          projRecord.editedDate._ &&
          ((projRecord.projectType._ === 'CHAMBER' && projRecord.outputFileURL._) ||
            (projRecord.projectType._ === 'PIPE' && projRecord.outputFileURLPipe._))
        ) {
          projectName = projectName.replace(' ', '_'); //TODO: replace all with regexp
          const blobService = MiniCADAPI.buildBlobService(blobToken);
          const outputMeta = await new Promise((resolve, reject) => {
            blobService.getBlobProperties(
              partitionKey,
              `${projectId}-automationfiles/${projectName}.zip`,
              (err, result, response) => {
                if (err) reject(err);
                resolve(result);
              },
            );
          });
          if (outputMeta) {
            const lastModified = new Date(outputMeta.lastModified);
            console.log('lastModified:', lastModified, ' | Edited Date:', projRecord.editedDate._);
            if (lastModified < projRecord.editedDate._) result = true;
          }
        }
      }
    } catch (error) {
      console.error(`MiniCADAPI.latestOutputExists() :: ${error.message}`);
    }
    return result;
  }

  static async getGeolocation() {
    let result;
    try {
      let response = await fetch('https://geolocation-db.com/json/a9e48c70-8b22-11ed-8d13-bd165d1291e3');
      result = await response.json();
    } catch (e) {
      console.error(`ERROR - MULTIBED_COMMON_ENDPOINT - response not a json: ${e.message}`);
    }
    return result;
  }

  static async logAgreementLogRecord(agreementPayload, token) {
    const tableService = MiniCADAPI.buildTableService(token);
    const entGen = azure.TableUtilities.entityGenerator;

    let entity = {
      PartitionKey: entGen.String(agreementPayload.userId),
      RowKey: entGen.String(agreementPayload.userEmail + '_' + agreementPayload.agreementTimestamp),
    };

    if (agreementPayload.agreementTimestamp)
      entity.agreementTimestamp = entGen.DateTime(agreementPayload.agreementTimestamp);

    if (agreementPayload.IPAddress) entity.IPAddress = entGen.String(agreementPayload.IPAddress);

    if (agreementPayload.userEmail) entity.userEmail = entGen.String(agreementPayload.userEmail);

    if (agreementPayload.fullName) entity.fullName = entGen.String(agreementPayload.fullName);

    if (agreementPayload.givenName) entity.givenName = entGen.String(agreementPayload.givenName);

    if (agreementPayload.surname) entity.surname = entGen.String(agreementPayload.surname);

    if (agreementPayload.country_name) entity.country_name = entGen.String(agreementPayload.country_name);

    if (agreementPayload.latitude) entity.latitude = entGen.String(agreementPayload.latitude);

    if (agreementPayload.longitude) entity.longitude = entGen.String(agreementPayload.longitude);

    if (agreementPayload.state) entity.state = entGen.String(agreementPayload.state);

    if (agreementPayload.city) entity.city = entGen.String(agreementPayload.city);

    if (agreementPayload.mobilePhone) entity.mobilePhone = entGen.String(agreementPayload.mobilePhone);

    let result;

    result = await new Promise((resolve, reject) => {
      tableService.insertEntity(AGREEMENT_LOG_STORAGE_NAME, entity, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve(entity);
        }
      });
    });

    return result;
  }

  // Set Users department as Cultec
  static async setB2cUsersDepartment(token, userId) {
    let client = MicrosoftGraph.Client.init({
      defaultVersion: 'v1.0',
      authProvider: (done) => {
        done(null, token);
      },
    });

    let userUpdated = await client.api(`/users/${userId}`)
      .version('beta')
      .patch({
            department: 'Cultec',
          });

    return userUpdated;
  }

  // Set Users department as Cultec
  static async getCultecUsers(token) {
    const users = await new Promise(async (resolve) => {
      const PageIterator = MicrosoftGraph.PageIterator;
      let client = MicrosoftGraph.Client.init({
        defaultVersion: 'v1.0',
        authProvider: (done) => {
          done(null, token);
        },
      });

      let response = await client
        .api('/users')
        .filter("department eq 'Cultec'")
        .version('beta')
        .select(
          'id',
          'givenName',
          'surname',
          'state',
          'city',
          'country',
          'identities',
          'companyName',
          'mobilePhone',
          'createdDateTime',
          'signInSessionsValidFromDateTime',
          'department',
          //'*'
        )
        .top(999)
        .get();
      let users = [];
      let pageIterator = new PageIterator(client, response, (data) => {
        users.push(data);
        if (pageIterator.nextLink === undefined && pageIterator.collection.length === 0) {
          resolve(users);
          return false;
        }
        return true;
      });
      pageIterator.iterate();
    });
    return users;
  }

  // multibed and sub project - EOB
}



// WEBPACK FOOTER //
// ./src/lib/MiniCADAPI.js